import React, {useEffect, useRef, useState, useMemo} from "react";
import {useLocation, Link} from "react-router-dom";
import {GoogleIcon, IconImg, IconVideo, YandexIcon} from "../utils/Icons";
import {authYandex, authGoogle, logout} from "../header/Header";
import {correctNumber, onlyText, textDefise, defenseFromManyRequest, AlertNoCancel} from "../crm/feedback";
import {Model3dButton, Model3dForm} from "../utils/Model3d";
import {fetchRequestFeedback} from "../../api/crm/feedback";
import SeoHelmet from "../utils/seo";
import Navigation from "../utils/Navigation";
import {findElementByLink, findParentWithConfigurator} from "../utils/siteStructure";
import siteStructure from "../siteStructure.json";
import description from "../productDescription.json";
import {PropertyList} from "../utils/utils";
import '../../css/productCard.css';
import {htmlConverter} from "../news/News";
import {useTheme} from "../header/Header";

const getImage = (path) => {
    if (!path) return "";
    const clean = String(path).replace(/^\.?\//, "");
    return `/images/${clean}`;
}

export function ProductPhoto({images}) {
    const [mainImage, setMainImage] = useState(images[0]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [thumbnailIndex, setThumbnailIndex] = useState(0); // Индекс начала видимых миниатюр
    const thumbnailsPerPage = 6; // Количество миниатюр на одной "странице"
    const { theme } = useTheme()

    const videoRef = useRef(null)

    const handleThumbnailClick = (image, index) => {
        setMainImage(image);
        setCurrentIndex(index);
    };

    const handleMainImageClick = () => {
        setIsModalOpen(true);
    };

    const handleMainVideoClick = () => {

        if (!videoRef.current.paused) {
            videoRef.current.pause();
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleNextImage = () => {
        const nextIndex = (currentIndex + 1) % images.length;
        setMainImage(images[nextIndex]);
        setCurrentIndex(nextIndex);
    };

    const handlePrevImage = () => {
        const prevIndex = (currentIndex - 1 + images.length) % images.length;
        setMainImage(images[prevIndex]);
        setCurrentIndex(prevIndex);
    };

    const handleNextThumbnails = () => {
        if (thumbnailIndex + thumbnailsPerPage < images.length) {
            setThumbnailIndex(thumbnailIndex + thumbnailsPerPage);
        }
    };

    const handlePrevThumbnails = () => {
        if (thumbnailIndex - thumbnailsPerPage >= 0) {
            setThumbnailIndex(thumbnailIndex - thumbnailsPerPage);
        }
    };

    const visibleThumbnails = images.slice(
        thumbnailIndex,
        thumbnailIndex + thumbnailsPerPage
    );

    const isVideo = (file) => {
        return file? file.endsWith('.webm') : false
    };

    useEffect(()=>{
        setMainImage(images[0])
    }, [images])

    return (
        <>
            {isVideo(mainImage) ? (
                <video
                    ref={videoRef}
                    src={mainImage}
                    className={`main-image ${theme}`}
                    controls
                    onClick={(e) => {
                        e.preventDefault()
                        handleMainVideoClick()
                    } }
                />
            ) : (
                <img
                    src={mainImage}
                    alt="Основное изображение"
                    className={`main-image ${theme}`}
                    onClick={handleMainImageClick}
                />
            )}

            {/* Условие для отображения иконки */}
            {!isVideo(mainImage) && (
                <div className="icon-container" onClick={handleMainImageClick}>
                    <IconImg />
                </div>
            )}

            <div className={`thumbnails ${theme}`}>
                {thumbnailIndex > 0 && (
                    <button className="prev-thumbnails" onClick={handlePrevThumbnails}>
                        &#10094;
                    </button>
                )}
                {visibleThumbnails.map((image, index) => (
                    isVideo(image) ? (
                        <div className={
                            currentIndex === thumbnailIndex + index
                                ? "thumbnail-video-wrapper active"
                                : "thumbnail-video-wrapper"
                        } key={thumbnailIndex + index}
                             onClick={() => handleThumbnailClick(image, thumbnailIndex + index)}>
                            <video
                                src={image}
                                className="thumbnail-video"
                            />
                            <IconVideo/>
                        </div>
                    ) : (
                        <img
                            key={thumbnailIndex + index}
                            src={image}
                            alt={`Миниатюра ${thumbnailIndex + index + 1}`}
                            className={
                                currentIndex === thumbnailIndex + index
                                    ? "thumbnail active"
                                    : "thumbnail"
                            }
                            onClick={() => handleThumbnailClick(image, thumbnailIndex + index)}
                        />
                    )
                ))}
                {thumbnailIndex + thumbnailsPerPage < images.length && (
                    <button className="next-thumbnails" onClick={handleNextThumbnails}>
                        &#10095;
                    </button>
                )}
            </div>

            {isModalOpen && (
                <div className="modal">
                    <div className={`modal-content ${theme}`}>
                        <span className="close" onClick={handleCloseModal}>&times;</span>
                        {isVideo(mainImage) ? (
                            <video
                                src={mainImage}
                                className="modal-video"
                                controls
                            />
                        ) : (
                            <img
                                src={mainImage}
                                alt={`Изображение ${currentIndex + 1}`}
                                className="modal-image"
                            />
                        )}
                        <button className="prev" onClick={handlePrevImage}> &#10094;</button>
                        <button className="next" onClick={handleNextImage}>&#10095;</button>
                    </div>
                </div>
            )}
        </>
    );
}

// Компоненты, которые пока используются, не должны быть удалены до переноса всего


// Компоненты для переноса данных в JSON
export function Product({path}) {

    const siteData = findElementByLink(siteStructure, path)
    const seo = siteData !== null && siteData.seo? siteData.seo : {title:"", description:"", keywords:"", changefreq: 'yearly', priority: 0.4}
    const { theme } = useTheme()

    return (
        <div>
            <SeoHelmet
                title={seo.title}
                description={seo.description}
                keywords={seo.keywords}
            />
            <div className={`complexBack ${theme}`}> </div>
            <Navigation/>

            <Property data={siteData}/>

            <ProductDescription title={siteData.title} theme={theme}/>
        </div>
    )
}

export function Property({ data }) {
    const location = useLocation();
    const currentPath = location.pathname.replace(/^\/|\/$/g, '');

    const { hasConfigurator, configuratorType } = useMemo(() => {
        if (data.configurator && typeof data.configurator === 'string') {
            return { hasConfigurator: true, configuratorType: data.configurator };
        }
        const result = findParentWithConfigurator(siteStructure, currentPath);
        return { hasConfigurator: result.hasConfigurator, configuratorType: result.configuratorType };
    }, [data, currentPath]);

    const token = localStorage.getItem('token');
    const [formVisible, setFormVisible] = useState(false);
    const [showAutorisationForm, setShowAutorisationForm] = useState(false);
    const [modelVisible, setModelVisible] = useState(false);
    const { theme } = useTheme();

    const images = data.property.images ? data.property.images.map(e => getImage(e)) : [];
    const cardOrder = { name: data.title, type: data.type, image: getImage(data.image) };

    const cardTypeText = {
        product: 'о продукте',
        service: 'об услуге',
    };

    const cardOrderButtonProps = {
        token,
        cardTypeText,
        cardOrder,
        setFormVisible,
        setShowAutorisationForm,
        theme,
    };

    const orderFormProps = {
        formVisible,
        token,
        cardTypeText,
        cardOrder,
        setShowAutorisationForm,
        setFormVisible,
        showAutorisationForm,
    };

    return (
        <>
            <div
                key={data.title}
                className={`product-card ${theme} ${data.property.model ? 'product-card-model-margin' : ''} ${
                    modelVisible ? 'product-card-modelVisible' : ''
                }`}
            >
                <div className="product-card-left">
                    <ProductPhoto images={images} />
                    {data.property.model && (
                        <Model3dButton formVisible={modelVisible} setFormVisible={setModelVisible} />
                    )}
                </div>
                <div className="product-card-right">
                    <PropertyText data={data} theme={theme} />
                    <div className="product-card-order-model-wrapper">
                        {hasConfigurator ? (
                            <ConfiguratorCard configuratorType={configuratorType} />
                        ) : (
                            cardOrder && <CardOrderButton {...cardOrderButtonProps} />
                        )}
                    </div>
                    {cardOrder && <CardOrderForm {...orderFormProps} />}
                    {data.property.model && (
                        <Model3dForm
                            formVisible={modelVisible}
                            setFormVisible={setModelVisible}
                            model={data.property.model}
                        />
                    )}
                </div>
            </div>
        </>
    );
}

function PropertyText({data, theme}) {

    const title = data.property.title? data.property.title : data.title

    return (
        <div>
            <h1 className={`product-title ${theme}`}>{title}</h1>
            {data.property.text? <p className={`product-subtitle ${theme}`}>{data.property.text}</p> : null}
            {data.property.about? <AboutProduct aboutData={data.property.about} theme={theme}/> : null}
        </div>
    )
}

function AboutProduct({aboutData, theme}){

    return (
        <div className={`product-details ${theme} product-details-list`}>
            <h2>О продукте</h2>
             <PropertyList list={aboutData} theme={theme}/>
        </div>
    )
}

export function CardOrderButton({token, cardTypeText, cardOrder, setFormVisible, setShowAutorisationForm, theme}) {

    return (
        <div className={`product-card-order ${theme}`}>
            <div>
                <p>{`Cвяжитесь с нами, для уточнения подробностей ${cardTypeText[cardOrder.type]}.`}</p>

                <div className={"product-card-order-button"}>
                        <span onClick={()=> {
                            if(token !== "") {
                                setFormVisible(true)
                            } else {
                                setShowAutorisationForm(true)
                            }
                        }}>Сделать заявку</span>
                </div>
            </div>
        </div>
    )
}

function CardOrderForm({formVisible, token, cardTypeText, cardOrder, setShowAutorisationForm, setFormVisible, showAutorisationForm}){

    const { theme } = useTheme()
    const [showAlertNoCancel, setShowAlertNoCancel] = useState(false)

    const [alertNoCancelParams, setAlertNoCancelParams] = useState({
        tittle: "Ошибка",
        buttonText: "выйти",
        message: `текст ошибки`,
        function: null,
        arguments: null,
    })

    const orderFormProps = {
        token,
        cardTypeText,
        cardOrder,
        setShowAutorisationForm,
        setFormVisible,
        setShowAlertNoCancel,
        setAlertNoCancelParams,
        theme
    }

    useEffect(() => {
        if (!formVisible && !showAutorisationForm) return undefined

        const handleEscape = (event) => {
            if (event.key !== "Escape") return

            setShowAutorisationForm(false)
            setFormVisible(false)
        }

        document.addEventListener("keydown", handleEscape)
        return () => document.removeEventListener("keydown", handleEscape)
    }, [formVisible, showAutorisationForm, setShowAutorisationForm, setFormVisible])

    return (
        <>
            <div className={`product-card-order-formWrapper  ${formVisible? "product-card-order-formVisible" : ""}`}>

                <OrderForm {...orderFormProps}/>

                <div onClick={()=> {
                    setShowAutorisationForm(false)
                    setFormVisible(false)
                }} className={`darkSpace ${formVisible? "darkSpaceVisible" : ""}`}> </div>
            </div>

            <div className="dropdown-login" style={{display: `${showAutorisationForm ? "block" : "none"}`}}>
                <div className="buttonLogin" onClick={authYandex}>
                    <YandexIcon/>
                    Войти через Яндекс ID
                </div>
                <div className="buttonLogin" onClick={authGoogle}>
                    <GoogleIcon/>
                    Войти через Google ID
                </div>
            </div>

            <div onClick={()=> {
                setShowAutorisationForm(false)
            }} className={`darkSpace ${showAutorisationForm? "darkSpaceVisible" : ""}`}> </div>

            <AlertNoCancel showAlertConfirm={showAlertNoCancel} setShowAlertConfirm={setShowAlertNoCancel}
                           alertConfirmParams={alertNoCancelParams}/>
        </>
    )

}

export function OrderForm({token, cardTypeText, cardOrder, setShowAutorisationForm,
                       setFormVisible,  setShowAlertNoCancel, setAlertNoCancelParams, theme}) {

    const [loadManyRequest, setLoadManyRequest] = useState(false)

    const [inputFillCheck, setInputFillCheck] = useState(false)

    const [inputFields, setInputFields] = useState({
        token: token,
        object: cardOrder.name,
        text: "",
        contactPhone: "",
        city: "",
        name: "",
    })

    function handleOnChange(e, key) {
        const {value} = e.target
        setInputFields(prevState => ({
            ...prevState,
            [key]: key === "text"? value : onlyText(value)
        }))
    }

    function handleOnChangeCity(e, key) {
        const {value} = e.target
        setInputFields(prevState => ({
            ...prevState,
            [key]: textDefise(value)
        }))
    }

    function handleOnChangeNumber(e, key) {
        const {value} = e.target

        setInputFields(prevState => ({
            ...prevState,
            [key]: correctNumber(value)
        }))
    }

    function fillRequired(key) {

        if(inputFillCheck && inputFields[key] === "") {
            return "feedbackFormRequiredFill"
        }

        if(key === "contactPhone" && inputFillCheck && inputFields[key].length !== 12) {
            return "feedbackFormRequiredFill"
        }

        return null
    }

    async function sendRequest() {



        defenseFromManyRequest(setLoadManyRequest)

        if(!inputFields.token) {
            setShowAutorisationForm(true)
            setFormVisible(false)
            return
        }

        if(!inputFields.text || inputFields.contactPhone.length !== 12 || !inputFields.city || !inputFields.name) {
            setInputFillCheck(true)
            return
        }

        setInputFillCheck(false)
        setFormVisible(false)

        try {
            let answer = await fetchRequestFeedback(inputFields)
            if(answer === `пользователь с таким токеном не найден\n`) {
                setAlertNoCancelParams({
                    tittle: "Ошибка",
                    buttonText: "выйти",
                    message: answer,
                    function: logout,
                    arguments: null})
                setShowAlertNoCancel(true)
            }
            if(answer === "many request\n") {
                setAlertNoCancelParams({
                    tittle: "Ошибка",
                    buttonText: "продолжить",
                    message: "Разрешено отправлять не более одного запроса раз в 10 минут",
                    function: null,
                    arguments: null})
                setShowAlertNoCancel(true)
            }
            if(answer === "ok") {
                setAlertNoCancelParams({
                    tittle: "Сообщение",
                    buttonText: "продолжить",
                    message: "Ваш запрос успешно отправлен",
                    function: null,
                    arguments: null})
                setShowAlertNoCancel(true)
            }
        } catch (error) {
            console.log("error", error)
            if(error === "пользователь с таким токеном не найден") {
                setAlertNoCancelParams({
                    tittle: "Ошибка",
                    buttonText: "выйти",
                    message: error,
                    function: null,
                    arguments: null})
                setShowAlertNoCancel(true)
            }
        }
    }

    useEffect(()=>{
    }, [inputFields])

    return (
        <div className={`orderForm ${theme}`}>
            <div>
                <div className={"orderFormNameImage"}>
                    <div>
                        <img className={"orderFormImage"} src={cardOrder.image} alt="no-img"/>
                        <span>{inputFields.object}</span>
                    </div>

                    <div onClick={()=>{setFormVisible(false)}}>
                        <svg viewBox="0 0 23 23" width="70%" height="75%">
                            <path d="M21,4.46158263L19.5384174 3 12 10.5384174 4.46158263 3 3 4.46158263 10.5384174 12 3 19.5384174 4.46158263 21 12 13.4615826 19.5384174 21 21 19.5384174 13.4615826 12z"> </path>
                        </svg>
                    </div>
                </div>

                <div className={"feedbackFormText"}>
                    Чтобы сделать заявку или получить
                    подробную информацию у наших менеджеров, заполните форму и мы с вами свяжемся.
                </div>

                <div className={"orderInputGroup"}>
                    <input required maxLength="30" type="text" className={`orderInput ${fillRequired("name")}`} value={inputFields.name}
                           onChange={(e) => handleOnChange(e, "name")}/>
                    <label className={"orderPlaceholder"}>Ваше имя</label>
                </div>

                <input type="text" placeholder={"+7 (000) 000-00-00"} className={`orderInput ${fillRequired("contactPhone")}`} value={inputFields.contactPhone} onChange={(e) => handleOnChangeNumber(e, "contactPhone")}/>

                <div className={"orderInputGroup"}>
                    <input required maxLength="30" type="text" className={`orderInput ${fillRequired("city")}`} value={inputFields.city} onChange={(e) => handleOnChangeCity(e, "city")}/>
                    <label className={"orderPlaceholder"}>Город</label>
                </div>

                <div className={"orderInputGroup"}>
                    <textarea required maxLength="300" rows="5" cols="33" className={`orderInput ${fillRequired("text")}`}  value={inputFields.text} onChange={(e) => handleOnChange(e, "text")}> </textarea>
                    <label className={"orderPlaceholderTextArea"}>Вопрос {cardTypeText[[cardOrder.type]]}</label>
                </div>

                <div className={`feedbackRequestButton ${loadManyRequest? "noActiveButton" : ""}`} onClick={sendRequest}><span>Отправить заявку</span></div>
            </div>
        </div>
    )
}

function ProductDescription({title, theme}) {

    return (
        <div key={title} className={`product-description ${theme}`}>
            <h2>Описание</h2>
            {description[title]?
                description[title].content.map((e, i)=>{
                    return (
                        <React.Fragment key={`contentDescription${i}`}>
                            {htmlConverter(e)}
                        </React.Fragment>
                    )
                })
                : null}
        </div>
    )
}

function ConfiguratorCard({ configuratorType }) {
    const { theme } = useTheme();

    return (
        <div className={`product-card-order ${theme}`}>
            <div>
                <p>Соберите свою комплектацию в конфигураторе</p>
                <div className="product-card-order-button">
                    <Link
                        to={`/configurator/${configuratorType}`}
                        style={{
                            textDecoration: 'none',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <span>Конфигуратор</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}